import React, { useState, useEffect, useCallback, useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog, ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import {
  type ActionMenuItem,
  SyutGrid,
  type SyutGridColumn,
  SyutSelect,
} from 'components/ui';
import notificationService from 'services/notificationService';
import { useLedgerMappings, useMappingLookups, useTransactionMaps, useDeleteLedgerMapping } from 'hooks/api/useLedgerMappings';
import { propertyService } from 'api/services/propertyService';
import type { AccountingLedgerMap, AccountingLedgerMapSearchRequest, LedgerMap, LedgerMapLeg } from 'api/types';
import type { SelectOption } from 'components/ui/SyutSelect';
import LedgerMappingModal from 'components/modals/modules/ledger-mapping-module/LedgerMappingModal';
import TransactionMapModal from 'components/modals/modules/ledger-mapping-module/TransactionMapModal';

type ScreenTab = 'ledgers' | 'vouchers';

const sourceTypeFilterOptions: SelectOption[] = [
  { value: '', label: 'All Source Types' },
  { value: 'BANK_ACCOUNT', label: 'Bank Account' },
  { value: 'ACCOUNTING_ROLE', label: 'Accounting Role' },
];

interface LedgerMappingScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
  /** Fail-closed RBAC actions from admin.tbl_role_permissions. */
  allowedActions?: string[];
}

/** Compact one-line summary of a voucher mapping's legs for the grid. */
const summarizeLegs = (details: LedgerMapLeg[]): string =>
  details
    .slice()
    .sort((a, b) => a.sequenceNo - b.sequenceNo)
    .map(d => {
      const side = d.entryType === 'DEBIT' ? 'DR' : 'CR';
      if (d.mapType === 'BANK_ACCOUNT') return `${side} Bank`;
      if (d.mapType === 'PAYMENT_MODE') return `${side} ${d.mapKey}${d.ledgerCode ? `→${d.ledgerCode}` : ''}`;
      return `${side} ${d.mapKey}`;
    })
    .join('  ·  ');

const LedgerMappingScreen: React.FC<LedgerMappingScreenProps> = ({ moduleIcon, moduleName, allowedActions = [] }) => {
  const [activeTab, setActiveTab] = useState<ScreenTab>('ledgers');

  const [queryParams, setQueryParams] = useState<AccountingLedgerMapSearchRequest>({ page: 1, pageSize: 25 });
  const { data: mappings, loading: mappingsLoading, pagination, refetch: refetchMappings } = useLedgerMappings(queryParams);
  const { data: transactionMaps, loading: mapsLoading, refetch: refetchMaps } = useTransactionMaps();
  const { data: lookups } = useMappingLookups();
  const { deleteLedgerMapping, loading: isDeleting } = useDeleteLedgerMapping();

  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterSourceType, setFilterSourceType] = useState('');

  const [mappingModalOpen, setMappingModalOpen] = useState(false);
  const [mappingModalMode, setMappingModalMode] = useState<'create' | 'edit'>('create');
  const [selectedMapping, setSelectedMapping] = useState<AccountingLedgerMap | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const [voucherModalOpen, setVoucherModalOpen] = useState(false);
  const [voucherModalMode, setVoucherModalMode] = useState<'create' | 'edit'>('create');
  const [selectedVoucherMap, setSelectedVoucherMap] = useState<LedgerMap | null>(null);

  // Property names for the voucher tab (LedgerMapDto carries only propertyId).
  const [propertyNames, setPropertyNames] = useState<Record<string, string>>({});
  useEffect(() => {
    propertyService.getAll({ pageSize: 500, includeInactive: true })
      .then(res => {
        const map: Record<string, string> = {};
        res.data.forEach((p: any) => { map[p.id] = p.propertyName; });
        setPropertyNames(map);
      })
      .catch(() => { /* names fall back to the raw id */ });
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setQueryParams(prev => ({ ...prev, searchTerm: searchQuery || undefined, page: 1 }));
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handlePageChange = useCallback((pageIndex: number) => {
    setQueryParams(prev => ({ ...prev, page: pageIndex + 1 }));
  }, []);
  const handlePageSizeChange = useCallback((pageSize: number) => {
    setQueryParams(prev => ({ ...prev, pageSize, page: 1 }));
  }, []);

  // ------------------------------------------------------------------
  // Resolver rows (Ledger Mappings tab)
  // ------------------------------------------------------------------

  const handleAddMapping = useCallback(() => {
    setSelectedMapping(null);
    setMappingModalMode('create');
    setMappingModalOpen(true);
  }, []);

  const handleEditMapping = useCallback((item: AccountingLedgerMap) => {
    setSelectedMapping(item);
    setMappingModalMode('edit');
    setMappingModalOpen(true);
  }, []);

  const handleDeleteClick = useCallback((item: AccountingLedgerMap) => {
    setSelectedMapping(item);
    setDeleteModalOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!selectedMapping) return;
    try {
      await deleteLedgerMapping(selectedMapping.id, selectedMapping.rowVersion);
      setDeleteModalOpen(false);
      notificationService.toast.success(`Mapping for "${selectedMapping.sourceDisplay}" deleted.`);
      setSelectedMapping(null);
      refetchMappings();
    } catch (err: any) {
      const msg = err?.response?.data?.message;
      notificationService.toast.error(msg || 'An error occurred while deleting. Please try again.');
    }
  }, [selectedMapping, deleteLedgerMapping, refetchMappings]);

  const handleMappingSaved = useCallback(() => {
    setMappingModalOpen(false);
    setSelectedMapping(null);
    refetchMappings();
  }, [refetchMappings]);

  const mappingColumns: SyutGridColumn<AccountingLedgerMap>[] = useMemo(() => [
    {
      id: 'sourceType', header: 'Type', accessorKey: 'sourceType', size: 140, enableSorting: true,
      cell: ({ getValue }) => {
        const v = getValue() as string;
        const label = v === 'BANK_ACCOUNT' ? 'Bank Account' : v === 'ACCOUNTING_ROLE' ? 'Role' : v;
        return (
          <span className="badge" style={{ background: 'var(--bg-secondary)', color: 'var(--text-secondary)', border: '1px solid var(--border-color)' }}>
            {label}
          </span>
        );
      },
    },
    { id: 'sourceDisplay', header: 'Source', accessorKey: 'sourceDisplay', size: 260, enableSorting: true },
    {
      id: 'propertyName', header: 'Scope', accessorKey: 'propertyName', size: 170,
      cell: ({ getValue }) => {
        const v = getValue() as string | undefined;
        return v
          ? <span>{v}</span>
          : <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>System default</span>;
      },
    },
    {
      id: 'ledgerCode', header: 'Fairbooks Ledger', accessorKey: 'ledgerCode', size: 240, enableSorting: true,
      cell: ({ row }) => {
        const item = row.original;
        return (
          <span>
            <span style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{item.ledgerCode || '?'}</span>
            {item.ledgerName ? ` - ${item.ledgerName}` : ''}
          </span>
        );
      },
    },
    {
      id: 'notes', header: 'Notes', accessorKey: 'notes', size: 200,
      cell: ({ getValue }) => <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{(getValue() as string) || '-'}</span>,
    },
    {
      id: 'isActive', header: 'Status', accessorKey: 'isActive', size: 100,
      cell: ({ getValue }) => {
        const isActive = getValue() as boolean;
        return <span className={`badge badge-${isActive ? 'active' : 'inactive'}`}>{isActive ? 'Active' : 'Inactive'}</span>;
      },
    },
  ], []);

  const getMappingRowActions = useCallback((item: AccountingLedgerMap): ActionMenuItem[] => {
    const items: ActionMenuItem[] = [];
    if (allowedActions.includes('edit')) {
      items.push({ id: 'edit', label: 'Edit', icon: <IconSystem.edit size={18} />, onClick: () => handleEditMapping(item) });
    }
    if (allowedActions.includes('delete')) {
      items.push({ id: 'delete', label: 'Delete', icon: <IconSystem.trash size={18} />, onClick: () => handleDeleteClick(item), variant: 'danger' });
    }
    return items;
  }, [allowedActions, handleEditMapping, handleDeleteClick]);

  // ------------------------------------------------------------------
  // Voucher mappings (Transaction Maps tab)
  // ------------------------------------------------------------------

  const handleAddVoucherMap = useCallback(() => {
    setSelectedVoucherMap(null);
    setVoucherModalMode('create');
    setVoucherModalOpen(true);
  }, []);

  const handleEditVoucherMap = useCallback((item: LedgerMap) => {
    setSelectedVoucherMap(item);
    setVoucherModalMode('edit');
    setVoucherModalOpen(true);
  }, []);

  const handleVoucherMapSaved = useCallback(() => {
    setVoucherModalOpen(false);
    setSelectedVoucherMap(null);
    refetchMaps();
  }, [refetchMaps]);

  const voucherColumns: SyutGridColumn<LedgerMap>[] = useMemo(() => [
    { id: 'transactionType', header: 'Transaction Type', accessorKey: 'transactionType', size: 170, enableSorting: true },
    {
      id: 'accountingType', header: 'Voucher Type', accessorKey: 'accountingType', size: 130, enableSorting: true,
      cell: ({ getValue }) => <span style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{getValue() as string}</span>,
    },
    {
      id: 'propertyId', header: 'Scope', accessorKey: 'propertyId', size: 170,
      cell: ({ getValue }) => {
        const id = getValue() as string | undefined;
        return id
          ? <span>{propertyNames[id] || id}</span>
          : <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>System default</span>;
      },
    },
    {
      id: 'legs', header: 'Legs', size: 320,
      cell: ({ row }) => <span style={{ fontSize: '0.82rem' }}>{summarizeLegs(row.original.details)}</span>,
    },
    {
      id: 'description', header: 'Description', accessorKey: 'description', size: 240,
      cell: ({ getValue }) => <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{(getValue() as string) || '-'}</span>,
    },
  ], [propertyNames]);

  const getVoucherRowActions = useCallback((item: LedgerMap): ActionMenuItem[] => {
    const items: ActionMenuItem[] = [];
    if (allowedActions.includes('edit')) {
      items.push({ id: 'edit', label: 'Edit', icon: <IconSystem.edit size={18} />, onClick: () => handleEditVoucherMap(item) });
    }
    return items;
  }, [allowedActions, handleEditVoucherMap]);

  const filteredVoucherMaps = useMemo(() => {
    if (!searchQuery.trim()) return transactionMaps;
    const q = searchQuery.toLowerCase();
    return transactionMaps.filter(m =>
      m.transactionType.toLowerCase().includes(q)
      || m.accountingType.toLowerCase().includes(q)
      || (m.description || '').toLowerCase().includes(q));
  }, [transactionMaps, searchQuery]);

  // ------------------------------------------------------------------
  // Header
  // ------------------------------------------------------------------

  const handleExport = useCallback(() => {
    const rows = activeTab === 'ledgers'
      ? mappings.map(m => [
          m.sourceType, m.sourceDisplay, m.propertyName || 'System default',
          m.ledgerCode || '', m.ledgerName || '', m.notes || '', m.isActive ? 'Active' : 'Inactive',
        ])
      : filteredVoucherMaps.map(m => [
          m.transactionType, m.accountingType,
          m.propertyId ? (propertyNames[m.propertyId] || m.propertyId) : 'System default',
          summarizeLegs(m.details), m.description || '',
        ]);
    if (rows.length === 0) {
      notificationService.toast.warning('No data to export');
      return;
    }
    const headers = activeTab === 'ledgers'
      ? ['Source Type', 'Source', 'Scope', 'Ledger Code', 'Ledger Name', 'Notes', 'Status']
      : ['Transaction Type', 'Voucher Type', 'Scope', 'Legs', 'Description'];
    const csvContent = [headers, ...rows]
      .map(r => r.map(v => {
        const s = String(v ?? '');
        return s.includes(',') || s.includes('"') ? `"${s.replace(/"/g, '""')}"` : s;
      }).join(','))
      .join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `ledger_mappings_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  }, [activeTab, mappings, filteredVoucherMaps, propertyNames]);

  const actionButtons: ActionButton[] = useMemo(() => {
    const buttons: ActionButton[] = [];
    if (allowedActions.includes('add')) {
      buttons.push({
        id: 'add',
        icon: <IconSystem.add size={18} />,
        label: activeTab === 'ledgers' ? 'Add Mapping' : 'Add Voucher Mapping',
        onClick: activeTab === 'ledgers' ? handleAddMapping : handleAddVoucherMap,
        tooltip: activeTab === 'ledgers' ? 'Add Ledger Mapping' : 'Add Transaction Mapping',
        variant: 'icon',
      });
    }
    buttons.push({
      id: 'refresh', icon: <IconSystem.refresh size={18} />, label: 'Refresh',
      onClick: () => { refetchMappings(); refetchMaps(); }, tooltip: 'Refresh', variant: 'icon',
    });
    if (allowedActions.includes('export')) {
      buttons.push({ id: 'export', icon: <IconSystem.download size={18} />, label: '', onClick: handleExport, tooltip: 'Export to CSV', variant: 'icon' });
    }
    return buttons;
  }, [allowedActions, activeTab, handleAddMapping, handleAddVoucherMap, refetchMappings, refetchMaps, handleExport]);

  const handleFilterApply = useCallback(() => {
    setQueryParams(prev => ({ ...prev, sourceType: filterSourceType || undefined, page: 1 }));
  }, [filterSourceType]);

  const handleFilterClear = useCallback(() => {
    setFilterSourceType('');
    setQueryParams(prev => ({ ...prev, sourceType: undefined, page: 1 }));
  }, []);

  const tabButtonStyle = (tab: ScreenTab): React.CSSProperties => ({
    padding: '0.4rem 0.9rem',
    border: 'none',
    borderBottom: activeTab === tab ? '2px solid var(--primary-color, #2563eb)' : '2px solid transparent',
    background: 'none',
    cursor: 'pointer',
    fontSize: '0.82rem',
    fontWeight: activeTab === tab ? 600 : 400,
    color: activeTab === tab ? 'var(--text-primary)' : 'var(--text-secondary)',
  });

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Fairbooks Ledger Mapping"
        description="Map bank accounts, payment modes and roles to Fairbooks ledgers for advance posting"
        icon={moduleIcon && (IconSystem as any)[moduleIcon] ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 }) : <IconSystem.link size={18} />}
        searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: activeTab === 'ledgers' ? 'Search mappings...' : 'Search voucher mappings...' }}
        filterConfig={{ onClick: () => setFilterOpen(!filterOpen), isActive: filterOpen, appliedCount: (filterSourceType ? 1 : 0) }}
        actions={actionButtons}
      />

      <div style={{ display: 'flex', gap: '0.25rem', borderBottom: '1px solid var(--border-color)', padding: '0 0.75rem', marginTop: '6px' }}>
        <button style={tabButtonStyle('ledgers')} onClick={() => setActiveTab('ledgers')}>Ledger Mappings</button>
        <button style={tabButtonStyle('vouchers')} onClick={() => setActiveTab('vouchers')}>Voucher Mappings</button>
      </div>

      <div className="module-content">
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden', minHeight: 0 }}>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
            {activeTab === 'ledgers' ? (
              <SyutGrid
                data={mappings}
                columns={mappingColumns}
                enableSorting
                enablePagination
                manualPagination
                totalCount={pagination.totalItems}
                currentPage={(queryParams.page ?? 1) - 1}
                pageSize={queryParams.pageSize ?? 25}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                isLoading={mappingsLoading}
                rowActions={getMappingRowActions}
                emptyMessage="No ledger mappings yet. Add one so advance postings can resolve their Fairbooks ledgers."
                frozenRightColumns={['actions']}
              />
            ) : (
              <SyutGrid
                data={filteredVoucherMaps}
                columns={voucherColumns}
                enableSorting
                enablePagination
                defaultPageSize={25}
                pageSizeOptions={[10, 25, 50, 100]}
                isLoading={mapsLoading}
                rowActions={getVoucherRowActions}
                emptyMessage="No voucher mappings yet. Add one to define the voucher type and legs for advance postings."
                frozenRightColumns={['actions']}
              />
            )}
          </div>

          {filterOpen && activeTab === 'ledgers' && (
            <div className="search-panel">
              <div className="search-panel-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                  <IconSystem.filter size={14} />
                  <h3 className="search-panel-title">Filters</h3>
                </div>
                <button onClick={() => setFilterOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem', display: 'flex', alignItems: 'center', color: 'var(--text-muted)', borderRadius: '0.25rem' }} title="Close filters">
                  <IconSystem.x size={16} />
                </button>
              </div>
              <div className="search-panel-content">
                <div className="filter-section">
                  <SyutSelect
                    label="Source Type"
                    options={sourceTypeFilterOptions}
                    value={filterSourceType}
                    onChange={(val: string | string[] | null) => setFilterSourceType((val as string) || '')}
                    isClearable={false}
                    size="sm"
                  />
                </div>
              </div>
              <div className="filter-actions">
                <button className="btn btn-secondary" onClick={handleFilterClear}>Clear</button>
                <button className="btn btn-primary" onClick={handleFilterApply}>Apply</button>
              </div>
            </div>
          )}
        </div>
      </div>

      <LedgerMappingModal
        isOpen={mappingModalOpen}
        mode={mappingModalMode}
        record={selectedMapping}
        lookups={lookups}
        onSave={handleMappingSaved}
        onClose={() => setMappingModalOpen(false)}
      />

      <TransactionMapModal
        isOpen={voucherModalOpen}
        mode={voucherModalMode}
        record={selectedVoucherMap}
        lookups={lookups}
        onSave={handleVoucherMapSaved}
        onClose={() => setVoucherModalOpen(false)}
      />

      <ConfirmDialog
        open={deleteModalOpen}
        title="Confirm Delete"
        message={`Delete the mapping for "${selectedMapping?.sourceDisplay}"? New postings that need it will park as Manual Review until it is remapped.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={isDeleting}
        loadingMessage="Deleting..."
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteModalOpen(false)}
      />
    </div>
  );
};

export default LedgerMappingScreen;
